import { useState, useEffect, useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import { SOMATIC_CHECKIN_FEELINGS, ALINEA_PROFILES } from '../utils/constants.js';
import {
    getPerdonarseIntro,
    getPerdonarseOptionsForStep,
    getPerdonarseStepMeta,
    getPerdonarseSteps,
    mapPerdonarseToIncident,
    successMessageForValence,
    valenceFromFeelingKey,
} from '../data/epaPerdonarseOptions.js';
import './Epa.css';

const EPA_PROFILE_ORDER = ['constante', 'impulsivo', 'analitico', 'social'];
const EPA_PROFILE_COLORS = {
    constante: '#22c55e',
    impulsivo: '#ef4444',
    analitico: '#3b82f6',
    social: '#eab308',
};

function isEpaLeaderRole(rol) {
    return rol === 'admin' || rol === 'supervisor' || rol === 'superadmin';
}

export default function Epa() {
    const { user } = useAuth();
    const { tenantId } = useTenantScope();
    const isLeader = isEpaLeaderRole(user?.rol);
    const [tab, setTab] = useState(isLeader ? 'resumen' : 'escuchar');
    const [checkinFeeling, setCheckinFeeling] = useState('');
    const [checkinNeed, setCheckinNeed] = useState('');
    const [showPause, setShowPause] = useState(false);
    const [pauseTimer, setPauseTimer] = useState(120);
    const [lessons, setLessons] = useState([]);
    const [lastSomaticFeeling, setLastSomaticFeeling] = useState('');
    const [perdonarStep, setPerdonarStep] = useState(0);
    const [perdonarFeeling, setPerdonarFeeling] = useState('');
    const [perdonarContext, setPerdonarContext] = useState('');
    const [perdonarTakeaway, setPerdonarTakeaway] = useState('');
    const [perdonarAffirm, setPerdonarAffirm] = useState('');
    const [perdonarSubmitting, setPerdonarSubmitting] = useState(false);
    const [perdonarDone, setPerdonarDone] = useState(false);
    const [toast, setToast] = useState('');
    const [epaSummary, setEpaSummary] = useState(null);
    const [epaSummaryLoading, setEpaSummaryLoading] = useState(false);

    const profile = ALINEA_PROFILES[user?.perfil_alinea] || ALINEA_PROFILES.constante;

    const perdonarValence = perdonarFeeling ? valenceFromFeelingKey(perdonarFeeling) : null;
    const perdonarSteps = getPerdonarseSteps(perdonarValence || 'friccion');
    const perdonarStepMeta = getPerdonarseStepMeta(perdonarStep, perdonarFeeling);
    const perdonarIntro = getPerdonarseIntro(lastSomaticFeeling);
    const perdonarOptions = getPerdonarseOptionsForStep(perdonarStep, perdonarFeeling);

    useEffect(() => {
        api.getLessons().then(setLessons).catch(console.error);
    }, []);

    useEffect(() => {
        if (!isLeader || tab !== 'resumen') return undefined;
        let cancelled = false;
        setEpaSummaryLoading(true);
        api.getEpaCompanySummary()
            .then((data) => {
                if (!cancelled) setEpaSummary(data);
            })
            .catch((err) => {
                console.error(err);
                if (!cancelled) setEpaSummary(null);
            })
            .finally(() => {
                if (!cancelled) setEpaSummaryLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isLeader, tab, tenantId]);

    const profileChartData = useMemo(() => {
        if (!epaSummary?.company?.profiles) return [];
        return epaSummary.company.profiles
            .filter((p) => p.count > 0)
            .map((p) => ({
                nombre: ALINEA_PROFILES[p.type]?.name?.replace(/^El /, '') || p.type,
                cantidad: p.count,
                tipo: p.type,
            }));
    }, [epaSummary]);

    const checkinChartData = useMemo(() => {
        const c = epaSummary?.company?.checkins30;
        if (!c?.total) return [];
        return [
            { estado: 'Bien', cantidad: c.bien, key: 'bien' },
            { estado: 'Regular', cantidad: c.regular, key: 'regular' },
            { estado: 'Necesita parar', cantidad: c.mal, key: 'mal' },
            { estado: 'Indispuesto', cantidad: c.sick, key: 'sick' },
        ].filter((r) => r.cantidad > 0);
    }, [epaSummary]);

    useEffect(() => {
        if (!showPause || pauseTimer <= 0) return;
        const interval = setInterval(() => setPauseTimer(t => t - 1), 1000);
        return () => clearInterval(interval);
    }, [showPause, pauseTimer]);

    async function submitCheckin() {
        try {
            const data = await api.submitCheckin({ feeling: checkinFeeling, need: checkinNeed || null });
            setLastSomaticFeeling(checkinFeeling);
            if (data.showPause) {
                setShowPause(true);
                setPauseTimer(120);
            }
            showMsg(data.message);
            setCheckinFeeling('');
            setCheckinNeed('');
        } catch (err) {
            showMsg(err.message);
        }
    }

    async function handlePauseDone() {
        await api.recordPause();
        setShowPause(false);
        showMsg('🧘 Pausa registrada. Bien hecho.');
    }

    function resetPerdonarse() {
        setPerdonarStep(0);
        setPerdonarFeeling('');
        setPerdonarContext('');
        setPerdonarTakeaway('');
        setPerdonarAffirm('');
        setPerdonarDone(false);
    }

    useEffect(() => {
        if (tab !== 'perdonar') resetPerdonarse();
    }, [tab]);

    function selectPerdonarseOption(stepIndex, option) {
        setPerdonarAffirm(option.affirm);
        const nextFeeling = stepIndex === 0 ? option.key : perdonarFeeling;
        const nextContext = stepIndex === 1 ? option.key : perdonarContext;
        if (stepIndex === 0) {
            setPerdonarFeeling(option.key);
            setPerdonarContext('');
            setPerdonarTakeaway('');
        }
        if (stepIndex === 1) setPerdonarContext(option.key);
        if (stepIndex === 2) setPerdonarTakeaway(option.key);
        window.setTimeout(() => {
            if (stepIndex < 2) setPerdonarStep(stepIndex + 1);
            else submitPerdonarse(option.key, nextFeeling, nextContext);
        }, 650);
    }

    async function submitPerdonarse(
        takeawayKey = perdonarTakeaway,
        feelingKey = perdonarFeeling,
        contextKey = perdonarContext
    ) {
        setPerdonarSubmitting(true);
        try {
            const payload = mapPerdonarseToIncident({
                feelingKey,
                contextKey,
                takeawayKey: takeawayKey || perdonarTakeaway,
            });
            const data = await api.submitIncident(payload);
            showMsg(data?.message || successMessageForValence(payload.valence));
            setPerdonarDone(true);
            const updatedLessons = await api.getLessons();
            setLessons(updatedLessons);
        } catch (err) {
            showMsg(err.message || 'No se pudo guardar');
        } finally {
            setPerdonarSubmitting(false);
        }
    }

    function showMsg(msg) {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    }

    return (
        <div className="epa-page animate-in">
            <p className="text-sm text-muted mb-md" style={{ lineHeight: 1.5, maxWidth: '52rem' }}>
                <strong>EPA</strong> = Escucha · Procesamiento · Ajuste. Las pestañas Escucha, Procesa y Ajusta son
                metáforas de acompañamiento operativo (no diagnóstico clínico ni programa espiritual).
            </p>
            {/* Header Introspectivo */}
            <header className="epa-header glass-card">
                <div className="epa-header-content">
                    <div className="epa-profile-badge">
                        <span className="epa-profile-emoji">{profile.emoji}</span>
                        <div className="epa-profile-info">
                            <span className="text-xs font-bold uppercase tracking-wider opacity-70">Tu Maestría</span>
                            <h3 className="epa-profile-name">{profile.name}</h3>
                        </div>
                    </div>
                    <div className="epa-archetype-box">
                        <p className="epa-archetype">{profile.archetypeShort || profile.archetype}</p>
                        <p className="epa-inner-path">"{profile.inner_path}"</p>
                    </div>
                </div>
            </header>

            <div className="epa-layout mt-lg">
                <aside className="epa-sidebar">
                    <div className="epa-tabs-vertical">
                        {[
                            ...(isLeader
                                ? [{ key: 'resumen', label: 'Resumen', emoji: '📊', desc: 'Manada' }]
                                : []),
                            { key: 'escuchar', label: 'Escucha', emoji: '👂', desc: 'Sintonía' },
                            { key: 'perdonar', label: 'Procesa', emoji: '🤲', desc: 'Evolución' },
                            { key: 'adaptar', label: 'Ajusta', emoji: '🌊', desc: 'Fluidez' },
                        ].map(t => (
                            <button
                                key={t.key}
                                className={`epa-tab-v ${tab === t.key ? 'active' : ''}`}
                                onClick={() => setTab(t.key)}
                            >
                                <span className="epa-tab-emoji">{t.emoji}</span>
                                <div className="epa-tab-text">
                                    <span className="epa-tab-label">{t.label}</span>
                                    <span className="epa-tab-desc">{t.desc}</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="epa-daily-anchor glass-card mt-md animate-in" style={{ animationDelay: '0.2s' }}>
                        <h4 className="text-sm font-bold mb-xs">⚓ Anclaje del Día</h4>
                        <p className="text-xs text-muted">{profile.daily_anchor}</p>
                    </div>
                </aside>

                <main className="epa-main-content">
                    {tab === 'resumen' && isLeader && (
                        <div className="epa-section epa-resumen-section stagger">
                            <div className="glass-card epa-resumen-intro">
                                <h3>Mapa de la manada — perfiles y EPA</h3>
                                <p className="text-sm text-muted" style={{ margin: 0, lineHeight: 1.5 }}>
                                    {epaSummary?.scope === 'team'
                                        ? 'Vista de tu equipo: distribución de perfiles ALINEA y actividad EPA (últimos 30 días).'
                                        : 'Vista de toda la empresa: cómo se distribuyen los perfiles por área y el rastreo de Escucha, Procesa y Ajusta.'}
                                </p>
                            </div>

                            {epaSummaryLoading ? (
                                <div className="flex items-center gap-sm text-muted py-lg">
                                    <div className="spinner" style={{ width: 28, height: 28 }} />
                                    Cargando resumen…
                                </div>
                            ) : !epaSummary ? (
                                <p className="text-muted">No se pudo cargar el resumen.</p>
                            ) : (
                                <>
                                    <div className="epa-resumen-kpis">
                                        <div className="glass-card epa-resumen-kpi">
                                            <span className="epa-resumen-kpi-label">Colaboradores activos</span>
                                            <strong>{epaSummary.company.headcount}</strong>
                                        </div>
                                        <div className="glass-card epa-resumen-kpi">
                                            <span className="epa-resumen-kpi-label">Con perfil ALINEA</span>
                                            <strong>{epaSummary.company.withProfile}</strong>
                                        </div>
                                        <div className="glass-card epa-resumen-kpi">
                                            <span className="epa-resumen-kpi-label">Sin perfil aún</span>
                                            <strong>{epaSummary.company.withoutProfile}</strong>
                                        </div>
                                        <div className="glass-card epa-resumen-kpi">
                                            <span className="epa-resumen-kpi-label">Check-ins (30 días)</span>
                                            <strong>{epaSummary.company.checkins30?.total ?? 0}</strong>
                                        </div>
                                    </div>

                                    <div className="epa-resumen-charts grid-2">
                                        <div className="glass-card">
                                            <h4 className="epa-resumen-chart-title">Perfiles en la empresa</h4>
                                            {profileChartData.length === 0 ? (
                                                <p className="text-muted text-sm">Aún no hay perfiles registrados.</p>
                                            ) : (
                                                <div className="epa-resumen-chart-box">
                                                    <ResponsiveContainer width="100%" height={220}>
                                                        <BarChart data={profileChartData} layout="vertical" margin={{ left: 4, right: 12 }}>
                                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                            <XAxis type="number" allowDecimals={false} />
                                                            <YAxis type="category" dataKey="nombre" width={100} tick={{ fontSize: 11 }} />
                                                            <Tooltip />
                                                            <Bar dataKey="cantidad" radius={[0, 6, 6, 0]} maxBarSize={28}>
                                                                {profileChartData.map((row) => (
                                                                    <Cell key={row.tipo} fill={EPA_PROFILE_COLORS[row.tipo] || '#94a3b8'} />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            )}
                                            <ul className="epa-resumen-legend">
                                                {EPA_PROFILE_ORDER.map((type) => {
                                                    const row = epaSummary.company.profiles.find((p) => p.type === type);
                                                    const p = ALINEA_PROFILES[type];
                                                    if (!row?.count) return null;
                                                    return (
                                                        <li key={type}>
                                                            <span className="epa-resumen-dot" style={{ background: EPA_PROFILE_COLORS[type] }} />
                                                            {p.emoji} {p.name}: <strong>{row.count}</strong> ({row.pct}%)
                                                        </li>
                                                    );
                                                })}
                                                {epaSummary.company.withoutProfile > 0 ? (
                                                    <li>
                                                        <span className="epa-resumen-dot" style={{ background: '#94a3b8' }} />
                                                        Sin perfil: <strong>{epaSummary.company.withoutProfile}</strong>
                                                    </li>
                                                ) : null}
                                            </ul>
                                        </div>

                                        <div className="glass-card">
                                            <h4 className="epa-resumen-chart-title">Check-ins somáticos (30 días)</h4>
                                            {checkinChartData.length === 0 ? (
                                                <p className="text-muted text-sm">Sin check-ins en el período.</p>
                                            ) : (
                                                <div className="epa-resumen-chart-box">
                                                    <ResponsiveContainer width="100%" height={220}>
                                                        <BarChart data={checkinChartData}>
                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                            <XAxis dataKey="estado" tick={{ fontSize: 11 }} />
                                                            <YAxis allowDecimals={false} />
                                                            <Tooltip />
                                                            <Bar dataKey="cantidad" radius={[6, 6, 0, 0]} maxBarSize={48}>
                                                                {checkinChartData.map((row) => (
                                                                    <Cell
                                                                        key={row.key}
                                                                        fill={
                                                                            row.key === 'bien'
                                                                                ? '#22c55e'
                                                                                : row.key === 'regular'
                                                                                  ? '#eab308'
                                                                                  : '#ef4444'
                                                                        }
                                                                    />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            )}
                                            <p className="text-xs text-muted mt-sm" style={{ marginBottom: 0 }}>
                                                Registros «Procesa»: {epaSummary.company.incidents30} · Aprendizajes con lección:{' '}
                                                {epaSummary.company.lessons30}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="glass-card">
                                        <h4 className="epa-resumen-chart-title">Por área — perfiles y actividad EPA</h4>
                                        {epaSummary.byArea.length === 0 ? (
                                            <p className="text-muted text-sm">No hay colaboradores activos en áreas.</p>
                                        ) : (
                                            <div className="epa-area-grid">
                                                {epaSummary.byArea.map((area) => (
                                                    <div key={area.areaId} className="epa-area-card">
                                                        <div className="epa-area-card-head">
                                                            <strong>{area.areaName}</strong>
                                                            <span className="text-xs text-muted">
                                                                {area.headcount} {area.headcount === 1 ? 'persona' : 'personas'}
                                                            </span>
                                                        </div>
                                                        <div
                                                            className="epa-area-profile-bar"
                                                            title="Distribución de perfiles"
                                                            role="img"
                                                            aria-label={`Perfiles en ${area.areaName}`}
                                                        >
                                                            {EPA_PROFILE_ORDER.map((type) => {
                                                                const pct = area.profileBreakdown.find((p) => p.type === type)?.pct || 0;
                                                                if (pct <= 0) return null;
                                                                return (
                                                                    <span
                                                                        key={type}
                                                                        className="epa-area-profile-seg"
                                                                        style={{
                                                                            width: `${pct}%`,
                                                                            background: EPA_PROFILE_COLORS[type],
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                            {area.withoutProfile > 0 && area.headcount > 0 ? (
                                                                <span
                                                                    className="epa-area-profile-seg"
                                                                    style={{
                                                                        width: `${Math.round((area.withoutProfile / area.headcount) * 100)}%`,
                                                                        background: '#cbd5e1',
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </div>
                                                        <ul className="epa-area-profile-list">
                                                            {area.profileBreakdown
                                                                .filter((p) => p.count > 0)
                                                                .map((p) => (
                                                                    <li key={p.type}>
                                                                        {ALINEA_PROFILES[p.type]?.emoji}{' '}
                                                                        {ALINEA_PROFILES[p.type]?.name?.split('(')[0].trim()}: {p.count}
                                                                    </li>
                                                                ))}
                                                            {area.withoutProfile > 0 ? (
                                                                <li className="text-muted">Sin perfil: {area.withoutProfile}</li>
                                                            ) : null}
                                                        </ul>
                                                        <div className="epa-area-epa-stats text-xs text-muted">
                                                            <span>
                                                                Check-ins: {area.checkins30.total}
                                                                {area.checkins30.total > 0
                                                                    ? ` (🙂${area.checkins30.bien} · 💪${area.checkins30.regular} · 🛑${area.checkins30.mal} · 🤒${area.checkins30.sick || 0})`
                                                                    : ''}
                                                            </span>
                                                            <span>
                                                                Procesa: {area.incidents30} · Lecciones: {area.lessons30}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* E: ESCUCHARSE */}
                    {tab === 'escuchar' && (
                        <div className="epa-section stagger">
                            {showPause ? (
                                <div className="epa-pause glass-card">
                                    <div className="epa-pause-circle">
                                        <span className="epa-pause-timer">{Math.floor(pauseTimer / 60)}:{String(pauseTimer % 60).padStart(2, '0')}</span>
                                    </div>
                                    <h3 className="mt-md">Espacio de Calma</h3>
                                    <p className="text-muted">Cierra los ojos. Respira profundo. Tu mente es el cielo, tus pensamientos las nubes.</p>
                                    {pauseTimer <= 0 && (
                                        <button className="btn btn-primary mt-lg w-full" onClick={handlePauseDone}>
                                            ✅ Terminé mi pausa
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="glass-card">
                                    <div className="flex justify-between items-center mb-md">
                                        <h3>Check-in Somático</h3>
                                        <span className="badge badge-green">E: Escucha</span>
                                    </div>
                                    <p className="text-sm text-muted mb-lg">¿Cómo está tu cuerpo y tu energía en este momento?</p>
                                    
                                    <div className="survey-options grid-cols-3">
                                        {Object.entries(SOMATIC_CHECKIN_FEELINGS).map(([val, opt]) => (
                                            <button
                                                key={val}
                                                className={`btn-survey-compact ${checkinFeeling === val ? 'selected' : ''}`}
                                                onClick={() => setCheckinFeeling(val)}
                                            >
                                                <span className="emoji-lg">{opt.emoji}</span>
                                                <span className="label-sm">{opt.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {checkinFeeling === 'mal' && (
                                        <div className="mt-xl animate-in">
                                            <p className="text-sm font-bold mb-md">¿Qué necesita tu manada interior?</p>
                                            <div className="survey-options">
                                                {[
                                                    { key: 'descanso', emoji: '🧘', label: 'Una pausa consciente' },
                                                    { key: 'agua', emoji: '💧', label: 'Hidratación' },
                                                    { key: 'hablar_con_jefe', emoji: '🗣️', label: 'Conversación de apoyo' },
                                                ].map(opt => (
                                                    <button
                                                        key={opt.key}
                                                        className={`btn-survey ${checkinNeed === opt.key ? 'selected' : ''}`}
                                                        onClick={() => setCheckinNeed(opt.key)}
                                                    >
                                                        <span className="emoji">{opt.emoji}</span> {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {checkinFeeling && (checkinFeeling !== 'mal' || checkinNeed) && (
                                        <button className="btn btn-primary w-full mt-xl animate-fade" onClick={submitCheckin}>
                                            Registrar Sentir
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* P: PERDONARSE — ramas por valencia */}
                    {tab === 'perdonar' && (
                        <div className="epa-section stagger">
                            <div className="glass-card epa-perdonar-card">
                                <div className="flex justify-between items-center mb-md">
                                    <h3>{perdonarIntro.title}</h3>
                                    <span className="badge badge-yellow">P: Procesa</span>
                                </div>

                                {perdonarDone ? (
                                    <div className="epa-perdonar-done animate-in">
                                        <p className="epa-perdonar-done-emoji" aria-hidden>
                                            🌱
                                        </p>
                                        <h4 className="mb-sm">Registrado con respeto</h4>
                                        <p className="text-muted text-sm" style={{ lineHeight: 1.55 }}>
                                            Lo que sentiste quedó guardado de forma anónima para ti. No es una calificación ni un
                                            reporte contra nadie: es espacio para soltar carga y seguir con más claridad.
                                        </p>
                                        <button type="button" className="btn btn-primary w-full mt-lg" onClick={resetPerdonarse}>
                                            Hacer otro registro
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {perdonarStep === 0 && (
                                            <p className="epa-perdonar-intro text-sm text-muted mb-lg">{perdonarIntro.body}</p>
                                        )}

                                        {perdonarValence ? (
                                            <p className="text-sm text-muted mb-md" aria-live="polite">
                                                {perdonarValence === 'eje'
                                                    ? 'Rama: sostén (sin buscar un problema).'
                                                    : perdonarValence === 'carga'
                                                      ? 'Rama: carga del día.'
                                                      : 'Rama: momento de fricción.'}
                                            </p>
                                        ) : null}

                                        <div className="epa-perdonar-progress" aria-label="Paso del registro">
                                            {perdonarSteps.map((s, i) => (
                                                <span
                                                    key={s.key}
                                                    className={`epa-perdonar-dot ${i === perdonarStep ? 'active' : ''} ${i < perdonarStep ? 'done' : ''}`}
                                                />
                                            ))}
                                        </div>

                                        {perdonarStep < 3 && (
                                            <>
                                                <p className="epa-perdonar-why">{perdonarStepMeta.why}</p>
                                                <h4 className="epa-perdonar-question mb-md">
                                                    {perdonarStepMeta.title}
                                                </h4>

                                                {perdonarAffirm && (
                                                    <p className="epa-perdonar-affirm animate-fade" role="status">
                                                        {perdonarAffirm}
                                                    </p>
                                                )}

                                                <div className="epa-perdonar-grid" role="group" aria-label={perdonarStepMeta.title}>
                                                    {perdonarOptions.map((opt) => {
                                                        const selected =
                                                            (perdonarStep === 0 && perdonarFeeling === opt.key) ||
                                                            (perdonarStep === 1 && perdonarContext === opt.key) ||
                                                            (perdonarStep === 2 && perdonarTakeaway === opt.key);
                                                        return (
                                                            <button
                                                                key={opt.key}
                                                                type="button"
                                                                className={`epa-perdonar-option ${selected ? 'selected' : ''}`}
                                                                disabled={perdonarSubmitting}
                                                                onClick={() => selectPerdonarseOption(perdonarStep, opt)}
                                                            >
                                                                <span className="epa-perdonar-option-emoji" aria-hidden>
                                                                    {opt.emoji}
                                                                </span>
                                                                <span className="epa-perdonar-option-label">{opt.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                {perdonarStep > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary w-full mt-md"
                                                        disabled={perdonarSubmitting}
                                                        onClick={() => {
                                                            setPerdonarAffirm('');
                                                            setPerdonarStep((s) => Math.max(0, s - 1));
                                                        }}
                                                    >
                                                        ← Paso anterior
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {perdonarSubmitting && (
                                            <p className="text-center text-muted mt-md">Guardando…</p>
                                        )}
                                    </>
                                )}
                            </div>

                            {lessons.length > 0 && (
                                <div className="mt-xl">
                                    <h4 className="mb-md flex items-center gap-sm">✨ Aprendizajes del equipo (anónimos)</h4>
                                    <div className="epa-lessons-grid">
                                        {lessons.slice(0, 4).map((l, i) => (
                                            <div key={i} className="glass-card epa-lesson-card">
                                                <p className="text-sm">{l.lesson_learned || l.what_happened}</p>
                                                <div className="flex justify-between items-center mt-md pt-md border-t border-glass">
                                                    <span className="text-xs opacity-60">Área: {l.area_name}</span>
                                                    <span className="text-xs opacity-40">
                                                        {new Date(l.incident_date).toLocaleDateString('es')}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* A: ADAPTARSE */}
                    {tab === 'adaptar' && (
                        <div className="epa-section animate-in">
                            <div className="glass-card">
                                <div className="flex justify-between items-center mb-md">
                                    <h3>Ajusta ante el cambio</h3>
                                    <span className="badge badge-red">A: Ajusta</span>
                                </div>
                                <div className="epa-adapt-hero">
                                    <div className="epa-adapt-icon">🌊</div>
                                    <p className="epa-adapt-quote">"El cambio no te quita lo que sabes. Te da algo nuevo que aprender."</p>
                                </div>
                                
                                <div className="epa-adapt-info mt-xl">
                                    <p className="text-sm">
                                        Frente a un cambio en la planta, tu liderazgo te apoyará con el <strong>Ejercicio de Anclaje</strong>.
                                    </p>
                                    
                                    <div className="epa-anchor-card mt-lg">
                                        <div className="epa-anchor-dot" />
                                        <div className="epa-anchor-content">
                                            <p className="text-md font-bold italic">"¿Qué habilidad nuestra sigue siendo la misma?"</p>
                                            <p className="text-xs text-muted mt-xs">
                                                Identifica tu maestría central para que el cambio no te desplace.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {toast && <div className="toast success animate-slide-up">{toast}</div>}
        </div>
    );
}
